import { MarkdownParser } from '../utils/jsmind.markdown';
import {
  FlashMapNodeMetricTooltip,
  FlashMapNodeMetricValue,
  FlashMapRootMetricChart,
} from '../models/types';
import { NodeRenderer, NodeRenderContext, NodeRenderResult, continueChain } from './node-renderer';

const metricsCache = new WeakMap<HTMLElement, string>();
const relayoutPending = new WeakSet<object>();
const contrastObservers = new WeakMap<HTMLElement, MutationObserver>();

interface RgbColor {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

const METRICS_LIGHT_COLOR = '#ffffff';
const METRICS_DARK_COLOR = '#111111';
const DUE_DARK_COLOR = '#6f1520';
const DUE_GRAPHITE_COLOR = '#1f2937';
const DUE_LIGHT_COLOR = '#f8fafc';

export class NodeMetricsRenderer implements NodeRenderer {
  render(context: NodeRenderContext): NodeRenderResult {
    const { jm, ele, node, options, getNodeMetrics } = context;
    if (!(ele instanceof HTMLElement)) {
      return continueChain;
    }

    const metric = this.normalizeMetric(getNodeMetrics?.(node.id));
    const metricsText = metric?.text ?? '';
    const dueText = metric?.dueText ?? '';
    const existingMetrics = ele.querySelector('.jmnode-metrics') as HTMLElement | null;

    if (!metricsText && !dueText) {
      if (existingMetrics) {
        existingMetrics.remove();
        contrastObservers.get(ele)?.disconnect();
        contrastObservers.delete(ele);
        this.scheduleRelayout(jm);
      }
      metricsCache.delete(ele);
      ele.classList.remove('has-node-metrics');
      return continueChain;
    }

    const metricsKey = `${options.support_html ? '1' : '0'}|${dueText}|${metricsText}|${JSON.stringify(metric?.tooltip ?? null)}|${JSON.stringify(metric?.rootChart ?? null)}`;
    if (metricsCache.get(ele) === metricsKey && existingMetrics) {
      ele.classList.add('has-node-metrics');
      this.applyMetricColors(ele, existingMetrics, Boolean(dueText));
      return continueChain;
    }

    const metricsElement = existingMetrics ?? document.createElement('span');
    metricsElement.className = 'jmnode-metrics';
    this.renderMetricsText(metricsElement, dueText, metricsText, options.support_html === true);
    this.renderTooltip(metricsElement, metric?.tooltip ?? null, metric?.rootChart ?? null);

    if (!existingMetrics) {
      const indicators = ele.querySelector('.flashcard-indicators') as HTMLElement | null;
      if (indicators) {
        ele.insertBefore(metricsElement, indicators);
      } else {
        ele.appendChild(metricsElement);
      }
    }

    ele.classList.add('has-node-metrics');
    this.applyMetricColors(ele, metricsElement, Boolean(dueText));
    this.observeThemeChanges(ele, metricsElement, Boolean(dueText));
    metricsCache.set(ele, metricsKey);
    this.scheduleRelayout(jm);
    return continueChain;
  }

  private applyMetricColors(node: HTMLElement, metrics: HTMLElement, hasDueText: boolean): void {
    const background = this.resolveBackgroundColor(node);
    const metricsColor = this.getHighestContrastColor(background, [METRICS_LIGHT_COLOR, METRICS_DARK_COLOR]);
    metrics.style.setProperty('--jmnode-metrics-color', metricsColor);

    if (hasDueText) {
      metrics.style.setProperty(
        '--jmnode-metrics-due-color',
        this.getDueTextColor(background),
      );
    } else {
      metrics.style.removeProperty('--jmnode-metrics-due-color');
    }
    metrics.style.removeProperty('--jmnode-metrics-due-background');
    metrics.classList.remove('has-due-contrast-fallback');
  }

  private observeThemeChanges(node: HTMLElement, metrics: HTMLElement, hasDueText: boolean): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }
    contrastObservers.get(node)?.disconnect();
    const observer = new MutationObserver(() => this.applyMetricColors(node, metrics, hasDueText));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-bs-theme'] });
    if (document.body) {
      observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-bs-theme'] });
    }
    const mapRoot = node.closest('.flash-map-root');
    if (mapRoot) {
      observer.observe(mapRoot, { attributes: true, attributeFilter: ['class', 'data-bs-theme'] });
    }
    const nodes = node.closest('jmnodes');
    if (nodes) {
      observer.observe(nodes, { attributes: true, attributeFilter: ['class', 'style'] });
    }
    observer.observe(node, { attributes: true, attributeFilter: ['class', 'style'] });
    contrastObservers.set(node, observer);
  }

  private getDueTextColor(background: RgbColor): string {
    if (this.isRedBackground(background)) {
      const graphiteContrast = this.getContrastRatio(background, this.parseHexColor(DUE_GRAPHITE_COLOR));
      return graphiteContrast >= 4.5 ? DUE_GRAPHITE_COLOR : DUE_LIGHT_COLOR;
    }
    return this.getHighestContrastColor(background, [DUE_DARK_COLOR, DUE_LIGHT_COLOR]);
  }

  private isRedBackground(color: RgbColor): boolean {
    return color.red > color.green * 1.35 && color.red > color.blue * 1.2;
  }

  private resolveBackgroundColor(element: HTMLElement): RgbColor {
    const backgrounds: RgbColor[] = [];
    let current: HTMLElement | null = element;
    while (current) {
      const color = this.parseCssColor(getComputedStyle(current).backgroundColor);
      if (color && color.alpha > 0) {
        backgrounds.push(color);
      }
      current = current.parentElement;
    }

    return backgrounds.reverse().reduce(
      (composed, color) => this.composite(color, composed),
      { red: 255, green: 255, blue: 255, alpha: 1 },
    );
  }

  private parseCssColor(value: string): RgbColor | null {
    const match = value.match(/^rgba?\(([^)]+)\)$/i);
    if (!match) {
      return null;
    }
    const channels = match[1].split(',').map((channel) => Number.parseFloat(channel.trim()));
    if (channels.length < 3 || channels.slice(0, 3).some((channel) => !Number.isFinite(channel))) {
      return null;
    }
    return {
      red: Math.min(255, Math.max(0, channels[0])),
      green: Math.min(255, Math.max(0, channels[1])),
      blue: Math.min(255, Math.max(0, channels[2])),
      alpha: Math.min(1, Math.max(0, Number.isFinite(channels[3]) ? channels[3] : 1)),
    };
  }

  private parseHexColor(value: string): RgbColor {
    const hex = value.slice(1);
    return {
      red: Number.parseInt(hex.slice(0, 2), 16),
      green: Number.parseInt(hex.slice(2, 4), 16),
      blue: Number.parseInt(hex.slice(4, 6), 16),
      alpha: 1,
    };
  }

  private composite(foreground: RgbColor, background: RgbColor): RgbColor {
    const alpha = foreground.alpha + background.alpha * (1 - foreground.alpha);
    if (alpha === 0) {
      return { red: 255, green: 255, blue: 255, alpha: 0 };
    }
    return {
      red: (foreground.red * foreground.alpha + background.red * background.alpha * (1 - foreground.alpha)) / alpha,
      green: (foreground.green * foreground.alpha + background.green * background.alpha * (1 - foreground.alpha)) / alpha,
      blue: (foreground.blue * foreground.alpha + background.blue * background.alpha * (1 - foreground.alpha)) / alpha,
      alpha,
    };
  }

  private getHighestContrastColor(background: RgbColor, candidates: string[]): string {
    return candidates.reduce((best, candidate) =>
      this.getContrastRatio(background, this.parseHexColor(candidate)) >
      this.getContrastRatio(background, this.parseHexColor(best))
        ? candidate
        : best,
    );
  }

  private getContrastRatio(first: RgbColor, second: RgbColor): number {
    const firstLuminance = this.getRelativeLuminance(first);
    const secondLuminance = this.getRelativeLuminance(second);
    return (Math.max(firstLuminance, secondLuminance) + 0.05) / (Math.min(firstLuminance, secondLuminance) + 0.05);
  }

  private getRelativeLuminance(color: RgbColor): number {
    const channels = [color.red, color.green, color.blue].map((channel) => {
      const normalized = channel / 255;
      return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    });
    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  }

  private normalizeMetric(metrics: FlashMapNodeMetricValue): {
    text: string;
    dueText: string;
    tooltip: FlashMapNodeMetricTooltip | null;
    rootChart: FlashMapRootMetricChart | null;
  } | null {
    const text = typeof metrics === 'string' ? metrics : metrics?.text;
    const dueText = typeof metrics === 'object' && metrics !== null ? metrics.dueText : undefined;
    if (typeof text !== 'string' || (dueText !== undefined && typeof dueText !== 'string')) {
      return null;
    }
    const normalizedText = text.trim();
    const normalizedDueText = dueText?.trim() ?? '';
    if (!normalizedText && !normalizedDueText) {
      return null;
    }
    return {
      text: normalizedText,
      dueText: normalizedDueText,
      tooltip: this.normalizeTooltip(
        metrics && typeof metrics === 'object' ? metrics.tooltip : null,
      ),
      rootChart: this.normalizeRootChart(
        metrics && typeof metrics === 'object' ? metrics.rootChart : null,
      ),
    };
  }

  private normalizeRootChart(chart: FlashMapRootMetricChart | null | undefined): FlashMapRootMetricChart | null {
    if (!chart || !Array.isArray(chart.items) || chart.items.length === 0) {
      return null;
    }
    const items = chart.items
      .map((item) => ({
        label: this.toOptionalText(item?.label) ?? '',
        percentage: Math.min(100, this.toNonNegativeInteger(item?.percentage)),
      }))
      .filter((item) => item.label.length > 0);
    return items.length > 0
      ? { items, ariaLabel: this.toOptionalText(chart.ariaLabel) }
      : null;
  }

  private normalizeTooltip(tooltip: FlashMapNodeMetricTooltip | null | undefined): FlashMapNodeMetricTooltip | null {
    if (!tooltip) {
      return null;
    }
    const totalCards = this.toNonNegativeInteger(tooltip.totalCards);
    if (totalCards <= 0) {
      return null;
    }
    return {
      totalCards,
      studiedCards: Math.min(totalCards, this.toNonNegativeInteger(tooltip.studiedCards)),
      matureCards: Math.min(totalCards, this.toNonNegativeInteger(tooltip.matureCards)),
      masteryText: this.toOptionalText(tooltip.masteryText),
      studiedText: this.toOptionalText(tooltip.studiedText),
      ariaLabel: this.toOptionalText(tooltip.ariaLabel),
    };
  }

  private toOptionalText(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private toNonNegativeInteger(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  }

  private renderMetricsText(
    element: HTMLElement,
    dueText: string,
    metricsText: string,
    supportHtml: boolean,
  ): void {
    element.replaceChildren();
    if (dueText) {
      const dueElement = document.createElement('strong');
      dueElement.className = 'mindmap-node-metric-due';
      dueElement.textContent = dueText;
      element.appendChild(dueElement);
    }
    if (dueText && metricsText) {
      element.appendChild(document.createTextNode(' - '));
    }
    if (!metricsText) {
      return;
    }
    if (supportHtml) {
      const template = document.createElement('template');
      template.innerHTML = MarkdownParser.parse(metricsText);
      element.appendChild(template.content);
      return;
    }

    const lines = metricsText.split('\n');
    lines.forEach((line, index) => {
      if (index > 0) {
        element.appendChild(document.createElement('br'));
      }
      element.appendChild(document.createTextNode(line));
    });
  }

  private renderTooltip(
    element: HTMLElement,
    tooltip: FlashMapNodeMetricTooltip | null,
    rootChart: FlashMapRootMetricChart | null,
  ): void {
    const existingTooltip = element.querySelector('.jmnode-metrics-tooltip');
    existingTooltip?.remove();

    if (rootChart) {
      this.renderRootChartTooltip(element, rootChart);
      return;
    }

    if (!tooltip) {
      element.setAttribute('aria-hidden', 'true');
      element.removeAttribute('tabindex');
      element.removeAttribute('role');
      element.removeAttribute('aria-label');
      return;
    }

    const percentage = Math.min(100, Math.round((tooltip.matureCards / tooltip.totalCards) * 100));
    const tone = percentage <= 40 ? 'danger' : percentage <= 60 ? 'warning' : 'success';
    const tooltipElement = document.createElement('div');
    tooltipElement.className = 'jmnode-metrics-tooltip';
    tooltipElement.setAttribute('role', 'tooltip');

    const radial = document.createElement('span');
    radial.className = `jmnode-metrics-radial is-${tone}`;
    radial.style.setProperty('--jmnode-metrics-progress', `${percentage * 3.6}deg`);
    const radialValue = document.createElement('span');
    radialValue.textContent = `${percentage}%`;
    radial.appendChild(radialValue);

    const details = document.createElement('span');
    details.className = 'jmnode-metrics-tooltip-details';
    const dominance = document.createElement('strong');
    dominance.textContent = tooltip.masteryText ?? `${percentage}%`;
    const studied = document.createElement('span');
    studied.textContent =
      tooltip.studiedText ?? `${tooltip.studiedCards}/${tooltip.totalCards}`;
    details.append(dominance, studied);
    tooltipElement.append(radial, details);
    element.appendChild(tooltipElement);
    element.removeAttribute('aria-hidden');
    element.tabIndex = 0;
    element.setAttribute('role', 'img');
    element.setAttribute(
      'aria-label',
      tooltip.ariaLabel ?? `${percentage}%. ${tooltip.studiedCards}/${tooltip.totalCards}.`,
    );
  }

  private renderRootChartTooltip(element: HTMLElement, chart: FlashMapRootMetricChart): void {
    const tooltipElement = document.createElement('div');
    tooltipElement.className = 'jmnode-metrics-tooltip jmnode-root-metrics-chart';
    tooltipElement.setAttribute('role', 'tooltip');

    const list = document.createElement('div');
    list.className = 'jmnode-root-metrics-chart-list';
    for (const item of chart.items) {
      const row = document.createElement('div');
      row.className = 'jmnode-root-metrics-chart-row';
      const label = document.createElement('span');
      label.className = 'jmnode-root-metrics-chart-label';
      label.textContent = item.label;
      const track = document.createElement('span');
      track.className = 'jmnode-root-metrics-chart-track';
      const bar = document.createElement('span');
      bar.className = 'jmnode-root-metrics-chart-bar';
      bar.style.width = `${item.percentage}%`;
      track.appendChild(bar);
      const value = document.createElement('strong');
      value.className = 'jmnode-root-metrics-chart-value';
      value.textContent = `${item.percentage}%`;
      row.append(label, track, value);
      list.appendChild(row);
    }

    tooltipElement.appendChild(list);
    element.appendChild(tooltipElement);
    element.removeAttribute('aria-hidden');
    element.tabIndex = 0;
    element.setAttribute('role', 'img');
    element.setAttribute('aria-label', chart.ariaLabel ?? element.textContent?.trim() ?? '');
  }

  private scheduleRelayout(jm: unknown): void {
    const jmObj = jm as { layout?: { layout?: () => void }; view?: { relayout?: () => void } } | null;
    if (!jmObj || typeof jmObj !== 'object') {
      return;
    }
    if (relayoutPending.has(jmObj)) {
      return;
    }
    relayoutPending.add(jmObj);
    const run = () => {
      relayoutPending.delete(jmObj);
      if (typeof jmObj.layout?.layout === 'function') {
        jmObj.layout.layout();
      }
      if (typeof jmObj.view?.relayout === 'function') {
        jmObj.view.relayout();
      }
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  }
}
